import { TRINKET_MINOR_REGISTRY } from '../data/trinkets.config';
import { ITEMS } from '../data/inventory.config';
import { ItemType } from '../types/inventory.types';
import { useLoadoutStore } from '../store/loadoutSlice';
import { useSocketsStore } from '../store/socketsSlice';
import type { SocketSlots } from '../store/socketsSlice';
import { useInventoryStore } from '../store/inventorySlice';
import { iterateEquippedMinors } from './TrinketService';
import { dungeonService } from './DungeonService';
import type { ModifierId } from '../types/modifier.types';
import type { TrinketId } from '../types/trinket.types';
import { MODIFIER_REGISTRY } from '../data/modifiers.config';
import { netService } from './NetService';
import { makePlayerLoadout } from '../net/loadoutSync';
import { captureLoadoutSnapshot } from './LoadoutSourceService';
import { getClientToken } from '../net/reconnect';

/** A boost item carries exactly one modifier; the id mirrors `boost-<modifierId>`. */
function boostItemId(modifierId: ModifierId): string {
  return `boost-${modifierId}`;
}

// Re-run the full run-scoped build so a socket change applies (or reverts)
// immediately: modifiers mutate the cloned spell instances, which are rebuilt
// from base here. The player is the only socket owner.
function rebuildRunEffects(): void {
  const player = dungeonService.getPlayer();
  if (player) {
    dungeonService.buildPlayerRunEffects(player);
  }
  if (netService.isClient()) {
    netService.session?.broadcast(
      'reliable',
      makePlayerLoadout(captureLoadoutSnapshot(), getClientToken()),
    );
  }
}

export const SocketService = {
  getSockets(minorId: TrinketId): SocketSlots {
    return useSocketsStore.getState().getSocketed(minorId);
  },

  canSocket(minorId: TrinketId, modifierId?: ModifierId): boolean {
    const loadout = useLoadoutStore.getState();
    if (loadout.getMinorLevel(minorId) === null) {
      return false;
    }
    const isEquipped = loadout.equipped.slots.some(
      (s) => s.minorId === minorId,
    );
    if (!isEquipped) {
      return false;
    }
    const def = TRINKET_MINOR_REGISTRY[minorId];
    if (!def || !def.socketSlots) {
      return false;
    }
    const socketed = this.getSockets(minorId);
    const filled = socketed.filter((m) => m != null).length;
    if (filled >= def.socketSlots) {
      return false;
    }
    if (modifierId !== undefined && socketed.includes(modifierId)) {
      return false;
    }
    return true;
  },

  socketBoost(
    minorId: TrinketId,
    socketIndex: number,
    modifierId: ModifierId,
  ): boolean {
    if (!this.canSocket(minorId, modifierId)) {
      return false;
    }
    useSocketsStore.getState().socket(minorId, socketIndex, modifierId);
    return true;
  },

  unsocketBoost(minorId: TrinketId, modifierId: ModifierId): void {
    useSocketsStore.getState().unsocket(minorId, modifierId);
  },

  /**
   * Equip a boost from the inventory into a minor trinket socket: consumes the
   * boost item, sockets its modifier, and rebuilds run effects so it applies
   * immediately. Returns false (no mutation) if the slot can't take it or the
   * item isn't held.
   */
  equipBoostFromInventory(
    minorId: TrinketId,
    socketIndex: number,
    modifierId: ModifierId,
  ): boolean {
    if (!this.canSocket(minorId, modifierId)) {
      return false;
    }
    const inventory = useInventoryStore.getState();
    if (inventory.removeItem(boostItemId(modifierId), 1) < 1) {
      return false;
    }
    if (!this.socketBoost(minorId, socketIndex, modifierId)) {
      inventory.addItem(boostItemId(modifierId), 1);
      return false;
    }
    rebuildRunEffects();
    return true;
  },

  /**
   * Unequip a socketed boost back into the inventory: aborts (keeps it
   * socketed) when the inventory is full. Rebuilds run effects on success so
   * the modifier is reverted immediately.
   */
  unequipBoostToInventory(minorId: TrinketId, modifierId: ModifierId): boolean {
    const inventory = useInventoryStore.getState();
    if (inventory.addItem(boostItemId(modifierId), 1) > 0) {
      return false;
    }
    this.unsocketBoost(minorId, modifierId);
    rebuildRunEffects();
    return true;
  },

  /**
   * Iterates all socketed modifiers across currently equipped minors.
   * Yields { modifierId, spellId } — spellId may be null when the minor
   * has no spell attached to its slot.
   */
  iterateSocketedModifiers(): {
    minorId: TrinketId;
    modifierId: ModifierId;
    spellId: string | null;
  }[] {
    const out: ReturnType<typeof this.iterateSocketedModifiers> = [];
    const socketed = useSocketsStore.getState().socketed;
    for (const { modelId, slot } of iterateEquippedMinors()) {
      const modifierIds = socketed[modelId];
      if (!modifierIds) {
        continue;
      }
      for (const modifierId of modifierIds) {
        if (!modifierId || !MODIFIER_REGISTRY[modifierId]) {
          continue;
        }
        out.push({ minorId: modelId, modifierId, spellId: slot.spellId });
      }
    }
    return out;
  },

  /**
   * Run-end cleanup: empties every socket and destroys all boost items still
   * held in the inventory. Modifiers are intra-run only — lost on defeat,
   * voluntary exit, or victory alike.
   */
  clearRunModifiers(): void {
    useSocketsStore.getState().clearAll();
    const inventory = useInventoryStore.getState();
    for (const slot of inventory.slots) {
      if (slot && ITEMS[slot.itemId]?.type === ItemType.BOOST) {
        inventory.removeItem(slot.itemId, slot.quantity);
      }
    }
  },
};
